import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { MODULOS, type Modulo, type Ref } from "@vetbolso/contracts";
import { useEffect, useMemo, useState } from "react";
import type { z } from "zod";
import { api, ErroApi, ler, queryClient } from "../api.ts";
import type { BuscaNovaFicha } from "../buscas.ts";
import { useEu } from "../router.tsx";
import {
  Area,
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Carregando,
  Cartao,
  Dialogo,
  dataHora,
  Entrada,
  Selecao,
  Selo,
  SeloStatus,
  Vazio,
} from "../ui.tsx";
import { NOME_MODULO } from "./fichas.tsx";

// ---------- leitura do schema zod ----------
// O formulário sai do próprio schema do módulo: campos simples viram entradas,
// `refs` ganha o seletor de fatos e o resto fica em JSON.

type Def = { type: string; innerType?: No; element?: No; entries?: Record<string, string>; shape?: Record<string, No>; values?: unknown[] };
type No = { _zod: { def: Def } };

function desembrulhar(s: No): { s: No; opcional: boolean } {
  let atual = s;
  let opcional = false;
  while (["optional", "nullable", "default", "prefault"].includes(atual._zod.def.type) && atual._zod.def.innerType) {
    opcional = true;
    atual = atual._zod.def.innerType;
  }
  return { s: atual, opcional };
}

type TipoCampo =
  | { tipo: "texto" | "numero" | "booleano" | "json" | "refs" }
  | { tipo: "enum"; opcoes: string[] }
  | { tipo: "multi"; opcoes: string[] }
  | { tipo: "objeto"; forma: Record<string, No> }
  | { tipo: "lista"; forma: Record<string, No> };

function tipoDe(chave: string, s: No): TipoCampo {
  const d = s._zod.def;
  if (chave === "refs") return { tipo: "refs" };
  if (d.type === "string") return { tipo: "texto" };
  if (d.type === "number") return { tipo: "numero" };
  if (d.type === "boolean") return { tipo: "booleano" };
  if (d.type === "enum") return { tipo: "enum", opcoes: Object.values(d.entries ?? {}) };
  if (d.type === "object" && d.shape) return { tipo: "objeto", forma: d.shape };
  if (d.type === "array" && d.element) {
    const el = desembrulhar(d.element).s._zod.def;
    if (el.type === "enum") return { tipo: "multi", opcoes: Object.values(el.entries ?? {}) };
    if (el.type === "object" && el.shape) return { tipo: "lista", forma: el.shape };
  }
  return { tipo: "json" };
}

/** Rótulos legíveis das chaves mais comuns; as demais aparecem como estão. */
const ROTULOS: Record<string, string> = {
  especies: "espécies",
  apresentacoes: "Apresentações",
  indicacoes: "Indicações",
  contraindicacoes: "Contraindicações",
  mecanismo: "Mecanismo de ação",
  interacoes: "Interações",
  efeitos: "Efeitos adversos",
  alertas: "Alertas",
  concentracao: "concentração",
  farmaco: "fármaco",
};
const rotuloDe = (k: string) => ROTULOS[k] ?? k;

/** Item novo de uma lista: só os campos obrigatórios, para preencher. */
function itemModelo(forma: Record<string, No>) {
  const out: Record<string, unknown> = {};
  for (const [k, bruto] of Object.entries(forma)) {
    const { s, opcional } = desembrulhar(bruto);
    if (opcional) continue;
    const d = s._zod.def;
    out[k] = d.type === "enum" ? Object.values(d.entries ?? {})[0] : d.type === "array" ? [] : d.type === "number" ? 0 : "";
  }
  return out;
}

const formaDe = (m: Modulo) => (MODULOS[m].schema as unknown as { shape?: Record<string, No> }).shape;

/** Valor inicial de uma ficha nova: só os campos obrigatórios. */
function modelo(m: Modulo): Record<string, unknown> {
  const forma = formaDe(m) ?? {};
  const out: Record<string, unknown> = {};
  for (const [k, bruto] of Object.entries(forma)) {
    const { s, opcional } = desembrulhar(bruto);
    if (opcional) continue;
    const d = s._zod.def;
    out[k] =
      d.type === "string"
        ? ""
        : d.type === "number"
          ? 0
          : d.type === "boolean"
            ? false
            : d.type === "enum"
              ? Object.values(d.entries ?? {})[0]
              : d.type === "array"
                ? []
                : d.type === "literal"
                  ? d.values?.[0]
                  : d.type === "object" || d.type === "record"
                    ? {}
                    : null;
  }
  return out;
}

const json = (v: unknown) => JSON.stringify(v, null, 2);
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

// ---------- campos ----------

function CampoJson({ valor, aoMudar, linhas = 6 }: { valor: unknown; aoMudar: (v: unknown) => void; linhas?: number }) {
  const [texto, setTexto] = useState(() => (valor === undefined ? "" : json(valor)));
  const [erro, setErro] = useState<string | null>(null);
  const chave = JSON.stringify(valor);
  // biome-ignore lint/correctness/useExhaustiveDependencies: sincroniza só quando o valor muda por fora
  useEffect(() => {
    setTexto(valor === undefined ? "" : json(valor));
    setErro(null);
  }, [chave]);
  return (
    <div className="flex flex-col gap-1">
      <Area
        rows={Math.min(24, Math.max(linhas, texto.split("\n").length))}
        value={texto}
        spellCheck={false}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => {
          if (!texto.trim()) return aoMudar(undefined);
          try {
            aoMudar(JSON.parse(texto));
            setErro(null);
          } catch (e) {
            setErro(`JSON inválido: ${msg(e)}`);
          }
        }}
      />
      {erro ? <span className="text-xs text-danger">{erro}</span> : null}
    </div>
  );
}

type FatoLinha = {
  id: string;
  fonteId: string | null;
  afirmacao: string;
  valor: string | null;
  unidade: string | null;
  status: string;
  localizador: string | null;
};

function BuscaFato({ aoEscolher }: { aoEscolher: (f: FatoLinha) => void }) {
  const [q, setQ] = useState("");
  const [termo, setTermo] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTermo(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ["fatos", termo],
    queryFn: () => ler(api.fatos.$get({ query: { q: termo } })),
    enabled: termo.length >= 2,
  });
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-line p-3">
      <Entrada
        type="search"
        placeholder="Buscar fato por id, afirmação ou valor (ex.: E.M03, carência)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {termo.length >= 2 ? (
        <ul className="flex max-h-64 flex-col gap-1 overflow-auto text-sm">
          {isFetching && !data ? <li className="text-muted">Buscando…</li> : null}
          {data?.length === 0 ? <li className="text-muted">Nenhum fato encontrado.</li> : null}
          {data?.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                disabled={!f.fonteId}
                onClick={() => aoEscolher(f)}
                className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-surface-2 disabled:opacity-50"
                title={f.fonteId ? "Adicionar como ref" : "Fato derivado sem fonte: cite os fatos de origem"}
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs font-semibold text-primary">{f.id}</span>
                  <span className="font-mono text-xs text-muted">{f.fonteId ?? "sem fonte"}</span>
                  {f.status !== "verificado" ? <Selo tom={f.status === "conflito" ? "perigo" : "alerta"}>{f.status}</Selo> : null}
                </span>
                <span className="text-xs text-ink">{f.afirmacao}</span>
                {f.valor ? (
                  <span className="font-mono text-xs text-muted">
                    {f.valor} {f.unidade ?? ""} {f.localizador ? `· ${f.localizador}` : ""}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Refs da ficha: cada número aponta para fonte, localizador e fato. */
function EditorRefs({ refs, aoMudar }: { refs: Ref[]; aoMudar: (r: Ref[]) => void }) {
  const [buscando, setBuscando] = useState(false);
  const muda = (i: number, p: Partial<Ref>) =>
    aoMudar(
      refs.map((r, j) => {
        if (j !== i) return r;
        const n = { ...r, ...p } as Ref;
        if (!n.pendente) delete n.pendente;
        if (!n.fato || (Array.isArray(n.fato) && !n.fato.length)) delete n.fato;
        return n;
      }),
    );
  return (
    <div className="flex flex-col gap-2">
      {refs.length === 0 ? <p className="text-sm text-danger">Sem refs: ficha clínica sem fonte é barrada na publicação.</p> : null}
      {refs.map((r, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a ordem das refs é a identidade delas
          key={i}
          className="grid grid-cols-1 gap-2 rounded-lg border border-line p-2 sm:grid-cols-[110px_1fr_160px_auto_auto] sm:items-center"
        >
          <Entrada aria-label="Fonte" className="font-mono" value={r.f} onChange={(e) => muda(i, { f: e.target.value.trim() })} />
          <Entrada
            aria-label="Localizador"
            placeholder="tabela, página, seção"
            value={r.loc}
            onChange={(e) => muda(i, { loc: e.target.value })}
          />
          <Entrada
            aria-label="Fato(s)"
            className="font-mono"
            placeholder="E.M03, E.M04"
            value={Array.isArray(r.fato) ? r.fato.join(", ") : (r.fato ?? "")}
            onChange={(e) => {
              const ids = e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
              muda(i, { fato: ids.length > 1 ? ids : ids[0] });
            }}
          />
          <label className="flex items-center gap-1.5 text-xs text-muted">
            <input type="checkbox" checked={!!r.pendente} onChange={(e) => muda(i, { pendente: e.target.checked })} />
            pendente
          </label>
          <Botao variante="fantasma" aria-label="Remover ref" onClick={() => aoMudar(refs.filter((_, j) => j !== i))}>
            ×
          </Botao>
        </div>
      ))}
      {buscando ? (
        <BuscaFato
          aoEscolher={(f) => {
            aoMudar([
              ...refs,
              { f: f.fonteId!, loc: f.localizador ?? "", fato: f.id, ...(f.status !== "verificado" ? { pendente: true } : {}) },
            ]);
            setBuscando(false);
          }}
        />
      ) : null}
      <div className="flex gap-2">
        <Botao onClick={() => setBuscando((b) => !b)}>{buscando ? "Fechar busca" : "Adicionar ref a partir de um fato"}</Botao>
        <Botao variante="fantasma" onClick={() => aoMudar([...refs, { f: "", loc: "" }])}>
          Ref manual
        </Botao>
      </div>
    </div>
  );
}

function FormularioFicha({
  modulo,
  valor,
  aoMudar,
}: {
  modulo: Modulo;
  valor: Record<string, unknown>;
  aoMudar: (v: Record<string, unknown>) => void;
}) {
  const forma = formaDe(modulo);
  const [bruto, setBruto] = useState(false);
  if (!forma || MODULOS[modulo].tipo === "doc" || bruto)
    return (
      <div className="flex flex-col gap-2">
        {forma && MODULOS[modulo].tipo === "lista" ? (
          <Botao variante="fantasma" className="self-start" onClick={() => setBruto(false)}>
            Voltar ao formulário
          </Botao>
        ) : null}
        <CampoJson valor={valor} aoMudar={(v) => aoMudar((v ?? {}) as Record<string, unknown>)} linhas={20} />
      </div>
    );

  const extras = Object.keys(valor).filter((k) => !(k in forma));
  return (
    <div className="flex flex-col gap-4">
      <CamposObjeto
        forma={forma}
        valor={valor}
        aoMudar={(v) => aoMudar({ ...v, ...Object.fromEntries(extras.map((k) => [k, valor[k]])) })}
      />
      {extras.length ? (
        <Grupo titulo="Campos fora do schema (JSON)" dica="Preservados como estão; o app pode ignorá-los.">
          <CampoJson
            valor={Object.fromEntries(extras.map((k) => [k, valor[k]]))}
            aoMudar={(v) => {
              const base = Object.fromEntries(Object.entries(valor).filter(([k]) => k in forma));
              aoMudar({ ...base, ...((v as object) ?? {}) });
            }}
            linhas={3}
          />
        </Grupo>
      ) : null}
      <Botao variante="fantasma" className="self-start" onClick={() => setBruto(true)}>
        Editar como JSON completo
      </Botao>
    </div>
  );
}

/** Campos de um objeto do schema: entradas simples, refs com o seletor de fatos, objetos e listas aninhados. */
function CamposObjeto({
  forma,
  valor,
  aoMudar,
}: {
  forma: Record<string, No>;
  valor: Record<string, unknown>;
  aoMudar: (v: Record<string, unknown>) => void;
}) {
  const set = (k: string, v: unknown) => {
    const n = { ...valor };
    if (v === undefined || v === "") delete n[k];
    else n[k] = v;
    aoMudar(n);
  };
  const campos = Object.entries(forma)
    .map(([k, b]) => ({ k, ...desembrulhar(b) }))
    .map((c) => ({ ...c, t: tipoDe(c.k, c.s) }));
  const simples = campos.filter((c) => !["json", "refs", "objeto", "lista"].includes(c.t.tipo));
  const blocos = campos.filter((c) => ["json", "objeto", "lista"].includes(c.t.tipo));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {simples.map(({ k, opcional, t }) => {
          const v = valor[k];
          const rotulo = opcional ? rotuloDe(k) : `${rotuloDe(k)} *`;
          if (t.tipo === "booleano")
            return (
              <label key={k} className="flex items-center gap-2 self-end pb-2 text-sm">
                <input type="checkbox" checked={!!v} onChange={(e) => set(k, e.target.checked || undefined)} />
                {rotuloDe(k)}
              </label>
            );
          if (t.tipo === "enum")
            return (
              <Campo key={k} rotulo={rotulo}>
                <Selecao value={String(v ?? "")} onChange={(e) => set(k, e.target.value || undefined)}>
                  {opcional ? <option value="">—</option> : null}
                  {t.opcoes.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </Selecao>
              </Campo>
            );
          if (t.tipo === "multi") {
            const sel = new Set((v as string[]) ?? []);
            return (
              <Campo key={k} rotulo={rotulo}>
                <div className="flex flex-wrap gap-x-3 gap-y-1 py-1">
                  {t.opcoes.map((o) => (
                    <label key={o} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={sel.has(o)}
                        onChange={(e) => {
                          const n = new Set(sel);
                          e.target.checked ? n.add(o) : n.delete(o);
                          set(k, n.size || !opcional ? t.opcoes.filter((x) => n.has(x)) : undefined);
                        }}
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </Campo>
            );
          }
          // Texto longo (afirmações de bulário, contexto) ganha área de várias linhas.
          const longo = t.tipo === "texto" && (k === "texto" || k === "efeito" || (typeof v === "string" && v.length > 60));
          return (
            <Campo key={k} rotulo={rotulo} className={longo ? "sm:col-span-2 lg:col-span-3" : undefined}>
              {longo ? (
                <Area rows={3} value={v == null ? "" : String(v)} onChange={(e) => set(k, e.target.value)} />
              ) : (
                <Entrada
                  type={t.tipo === "numero" ? "number" : "text"}
                  step="any"
                  className={t.tipo === "numero" ? "font-mono" : undefined}
                  value={v == null ? "" : String(v)}
                  onChange={(e) =>
                    set(k, t.tipo === "numero" ? (e.target.value === "" ? undefined : Number(e.target.value)) : e.target.value)
                  }
                />
              )}
            </Campo>
          );
        })}
      </div>

      {"refs" in forma ? (
        <Grupo
          titulo={desembrulhar(forma.refs as No).opcional ? "refs" : "refs *"}
          dica="Fonte, localizador e o fato conferido. Fato não verificado exige marcar pendente."
        >
          <EditorRefs refs={(valor.refs as Ref[]) ?? []} aoMudar={(r) => set("refs", r)} />
        </Grupo>
      ) : null}

      {blocos.map(({ k, opcional, t }) => {
        const titulo = opcional ? rotuloDe(k) : `${rotuloDe(k)} *`;
        if (t.tipo === "objeto") {
          const atual = valor[k] as Record<string, unknown> | undefined;
          return (
            <Grupo key={k} titulo={titulo}>
              {atual ? (
                <>
                  <CamposObjeto forma={t.forma} valor={atual} aoMudar={(v) => set(k, v)} />
                  {opcional ? (
                    <Botao variante="fantasma" className="self-start" onClick={() => set(k, undefined)}>
                      Remover {rotuloDe(k).toLowerCase()}
                    </Botao>
                  ) : null}
                </>
              ) : (
                <Botao className="self-start" onClick={() => set(k, itemModelo(t.forma))}>
                  Preencher {rotuloDe(k).toLowerCase()}
                </Botao>
              )}
            </Grupo>
          );
        }
        if (t.tipo === "lista")
          return (
            <Grupo key={k} titulo={titulo}>
              <EditorLista
                forma={t.forma}
                itens={(valor[k] as Record<string, unknown>[]) ?? []}
                aoMudar={(v) => set(k, v.length || !opcional ? v : undefined)}
              />
            </Grupo>
          );
        return (
          <Grupo key={k} titulo={`${titulo} (JSON)`} dica="Edite e saia do campo para aplicar.">
            <CampoJson valor={valor[k]} aoMudar={(v) => set(k, v)} linhas={3} />
          </Grupo>
        );
      })}
    </div>
  );
}

/** Bloco com título para editores compostos: um <label> não pode envolver vários controles. */
function Grupo({ titulo, dica, children }: { titulo: string; dica?: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-2 rounded-xl border border-line p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{titulo}</legend>
      {dica ? <p className="text-xs text-muted">{dica}</p> : null}
      {children}
    </fieldset>
  );
}

/** Lista de itens do schema: um cartão por item, com reordenar e remover. */
function EditorLista({
  forma,
  itens,
  aoMudar,
}: {
  forma: Record<string, No>;
  itens: Record<string, unknown>[];
  aoMudar: (v: Record<string, unknown>[]) => void;
}) {
  const mover = (i: number, d: number) => {
    const n = [...itens];
    const [x] = n.splice(i, 1);
    n.splice(i + d, 0, x as Record<string, unknown>);
    aoMudar(n);
  };
  return (
    <div className="flex flex-col gap-3">
      {itens.length === 0 ? <p className="text-sm text-muted">Nenhum item.</p> : null}
      {itens.map((it, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a posição é a identidade do item enquanto se edita
          key={i}
          className="flex flex-col gap-3 rounded-lg border border-line bg-surface-2 p-3"
        >
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold text-muted">#{i + 1}</span>
            <div className="ml-auto flex gap-1">
              <Botao variante="fantasma" aria-label="Subir item" disabled={i === 0} onClick={() => mover(i, -1)}>
                ↑
              </Botao>
              <Botao variante="fantasma" aria-label="Descer item" disabled={i === itens.length - 1} onClick={() => mover(i, 1)}>
                ↓
              </Botao>
              <Botao variante="fantasma" aria-label="Remover item" onClick={() => aoMudar(itens.filter((_, j) => j !== i))}>
                ×
              </Botao>
            </div>
          </div>
          <CamposObjeto forma={forma} valor={it} aoMudar={(v) => aoMudar(itens.map((x, j) => (j === i ? v : x)))} />
        </div>
      ))}
      <Botao className="self-start" onClick={() => aoMudar([...itens, itemModelo(forma)])}>
        Adicionar item
      </Botao>
    </div>
  );
}

/** Validação ao vivo com o mesmo validador da publicação. */
function useValidacao(modulo: Modulo, data: unknown) {
  const [erros, setErros] = useState<string[] | null>(null);
  const chave = JSON.stringify(data);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a chave serializada já cobre `data`
  useEffect(() => {
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await ler(api.validar.$post({ json: { modulo, data } }));
        if (vivo) setErros(r.erros);
      } catch (e) {
        if (vivo) setErros([msg(e)]);
      }
    }, 400);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [modulo, chave]);
  return erros;
}

function PainelValidacao({ erros }: { erros: string[] | null }) {
  if (!erros) return <p className="text-sm text-muted">Validando…</p>;
  if (!erros.length) return <Aviso tom="ok">Cadeia de auditoria completa: toda ref tem fonte, fato e localizador.</Aviso>;
  return (
    <Aviso>
      <p className="font-semibold">{erros.length} problema(s) de auditoria</p>
      <ul className="mt-1 list-disc pl-4 text-xs">
        {erros.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </Aviso>
  );
}

// ---------- páginas ----------

const pode = (papel: string | undefined, ...p: string[]) => !!papel && (papel === "admin" || p.includes(papel));

export function PaginaNovaFicha() {
  const { modulo } = useSearch({ strict: false }) as z.infer<typeof BuscaNovaFicha>;
  const navegar = useNavigate();
  const m = modulo as Modulo;
  const [valor, setValor] = useState(() => (m in MODULOS ? modelo(m) : {}));
  const erros = useValidacao(m, valor);
  const criar = useMutation({
    mutationFn: () => ler(api.fichas.$post({ json: { modulo: m, data: valor } })),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["fichas"] });
      navegar({ to: "/fichas/$id", params: { id: r.id } });
    },
  });
  if (!m || !(m in MODULOS) || MODULOS[m].tipo !== "lista")
    return (
      <Vazio>
        {m ? "Este módulo não tem fichas avulsas." : "Escolha um módulo para criar a ficha."}{" "}
        <Link to="/fichas" search={{}} className="font-semibold text-primary">
          Ir para Fichas
        </Link>
      </Vazio>
    );
  return (
    <>
      <Cabecalho
        titulo={`Nova ficha · ${NOME_MODULO[m]}`}
        sub="Nasce como rascunho. Só entra no app depois de aprovada por um revisor com CRMV."
        acoes={
          <Botao variante="primario" disabled={criar.isPending} onClick={() => criar.mutate()}>
            Criar rascunho
          </Botao>
        }
      />
      {criar.error ? <Aviso>{msg(criar.error)}</Aviso> : null}
      <PainelValidacao erros={erros} />
      <Cartao>
        <FormularioFicha modulo={m} valor={valor} aoMudar={setValor} />
      </Cartao>
    </>
  );
}

export function PaginaFicha() {
  const { id } = useParams({ from: "/area/fichas/$id" });
  const q = useQuery({ queryKey: ["ficha", id], queryFn: () => ler(api.fichas[":id"].$get({ param: { id } })) });
  if (q.error) return <Aviso>{q.error instanceof ErroApi && q.error.status === 404 ? "Ficha não encontrada." : msg(q.error)}</Aviso>;
  if (!q.data) return <Carregando />;
  return <EditorFicha key={`${q.data.ficha.id}@${q.data.ficha.versao}@${q.data.ficha.status}`} d={q.data} />;
}

type DadosFicha = Awaited<ReturnType<typeof lerFicha>>;
const lerFicha = (id: string) => ler(api.fichas[":id"].$get({ param: { id } }));

function EditorFicha({ d }: { d: DadosFicha }) {
  const { ficha } = d;
  const modulo = ficha.modulo as Modulo;
  const eu = useEu();
  const [valor, setValor] = useState(() => structuredClone(ficha.data) as Record<string, unknown>);
  const [revisando, setRevisando] = useState<null | "aprovar" | "devolver">(null);
  const [comentario, setComentario] = useState("");
  const [verDiff, setVerDiff] = useState(false);
  const erros = useValidacao(modulo, valor);
  const sujo = JSON.stringify(valor) !== JSON.stringify(ficha.data);
  const atualizar = () => {
    queryClient.invalidateQueries({ queryKey: ["ficha", ficha.id] });
    queryClient.invalidateQueries({ queryKey: ["fichas"] });
    queryClient.invalidateQueries({ queryKey: ["pendencias"] });
    queryClient.invalidateQueries({ queryKey: ["previa"] });
  };
  const p = { param: { id: ficha.id } };
  const salvar = useMutation({ mutationFn: () => ler(api.fichas[":id"].$put({ ...p, json: { data: valor } })), onSuccess: atualizar });
  const enviar = useMutation({ mutationFn: () => ler(api.fichas[":id"].enviar.$post(p)), onSuccess: atualizar });
  const arquivar = useMutation({ mutationFn: () => ler(api.fichas[":id"].arquivar.$post(p)), onSuccess: atualizar });
  const revisar = useMutation({
    mutationFn: (decisao: "aprovar" | "devolver") =>
      ler(
        api.fichas[":id"].revisar.$post({
          ...p,
          json: decisao === "aprovar" ? { decisao, comentario: comentario || undefined } : { decisao, comentario },
        }),
      ),
    onSuccess: () => {
      setRevisando(null);
      setComentario("");
      atualizar();
    },
  });
  const erroAcao = [salvar, enviar, arquivar].find((m) => m.error)?.error;
  const editor = pode(eu?.papel, "editor");
  const revisor = pode(eu?.papel, "revisor");
  const ultimoEditorSouEu = ficha.autorId === eu?.id;

  const diff = useMemo(() => {
    const a = (ficha.publicada ?? {}) as Record<string, unknown>;
    const b = ficha.data as Record<string, unknown>;
    return [...new Set([...Object.keys(a), ...Object.keys(b)])]
      .filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]))
      .map((k) => ({ k, antes: a[k], depois: b[k] }));
  }, [ficha.publicada, ficha.data]);

  return (
    <>
      <Cabecalho
        titulo={ficha.titulo}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            <Link to="/fichas" search={{ modulo }} className="hover:text-primary">
              {NOME_MODULO[modulo]}
            </Link>
            <span className="font-mono text-xs">{ficha.id}</span>
            <SeloStatus status={ficha.status} />
            <span className="text-xs">v{ficha.versao}</span>
            {ficha.publicada ? (
              diff.length ? (
                <Selo tom="destaque">difere da publicada</Selo>
              ) : (
                <Selo tom="ok">igual à publicada</Selo>
              )
            ) : (
              <Selo>nunca publicada</Selo>
            )}
          </span>
        }
        acoes={
          <>
            {editor && ficha.status !== "arquivada" ? (
              <Botao variante="primario" disabled={!sujo || salvar.isPending} onClick={() => salvar.mutate()}>
                {salvar.isPending ? "Salvando…" : sujo ? "Salvar rascunho" : "Salvo"}
              </Botao>
            ) : null}
            {editor && ficha.status === "rascunho" ? (
              <Botao
                disabled={sujo || enviar.isPending || !!(ficha.erros as string[]).length}
                onClick={() => enviar.mutate()}
                title={sujo ? "Salve antes de enviar" : undefined}
              >
                Enviar para revisão
              </Botao>
            ) : null}
            {revisor && ficha.status === "em_revisao" ? (
              <>
                <Botao
                  variante="primario"
                  disabled={!eu?.crmv || ultimoEditorSouEu}
                  title={
                    !eu?.crmv ? "Cadastre seu CRMV para aprovar" : ultimoEditorSouEu ? "Quem editou não aprova a própria edição" : undefined
                  }
                  onClick={() => setRevisando("aprovar")}
                >
                  Aprovar
                </Botao>
                <Botao onClick={() => setRevisando("devolver")}>Devolver</Botao>
              </>
            ) : null}
            {editor && ficha.status !== "arquivada" && MODULOS[modulo].tipo === "lista" ? (
              <Botao variante="fantasma" disabled={arquivar.isPending} onClick={() => arquivar.mutate()}>
                Arquivar
              </Botao>
            ) : null}
          </>
        }
      />
      {erroAcao ? <Aviso>{msg(erroAcao)}</Aviso> : null}
      {ficha.comentarioRevisao ? (
        <Aviso tom={ficha.status === "rascunho" ? "alerta" : "ok"}>
          <span className="font-semibold">{d.revisor?.nome ?? "Revisor"}:</span> {ficha.comentarioRevisao}
        </Aviso>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <PainelValidacao erros={erros} />
          <Cartao titulo="Conteúdo">
            <fieldset disabled={!editor || ficha.status === "arquivada"} className="min-w-0">
              <FormularioFicha modulo={modulo} valor={valor} aoMudar={setValor} />
            </fieldset>
          </Cartao>
          {ficha.publicada && diff.length ? (
            <Cartao
              titulo={`Mudanças em relação à versão publicada (${diff.length} campo(s))`}
              acoes={
                <Botao variante="fantasma" onClick={() => setVerDiff((v) => !v)}>
                  {verDiff ? "Ocultar" : "Mostrar"}
                </Botao>
              }
            >
              {verDiff ? (
                <div className="flex flex-col gap-3">
                  {diff.map(({ k, antes, depois }) => (
                    <div key={k} className="flex min-w-0 flex-col gap-1">
                      <span className="font-mono text-xs font-semibold">{k}</span>
                      <div className="grid gap-2 md:grid-cols-2">
                        <pre className="overflow-auto rounded-lg bg-danger-soft p-2 text-xs">
                          {antes === undefined ? "(ausente)" : json(antes)}
                        </pre>
                        <pre className="overflow-auto rounded-lg bg-primary-soft p-2 text-xs">
                          {depois === undefined ? "(removido)" : json(depois)}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </Cartao>
          ) : null}
        </div>

        <aside className="flex flex-col gap-4">
          <Cartao titulo="Responsáveis">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted">Última edição</dt>
              <dd>{d.autor?.nome ?? "—"}</dd>
              <dt className="text-muted">Revisão</dt>
              <dd>{d.revisor ? `${d.revisor.nome} · ${dataHora(ficha.revisadoEm)}` : "—"}</dd>
              <dt className="text-muted">Atualizada</dt>
              <dd>{dataHora(ficha.atualizadoEm)}</dd>
            </dl>
          </Cartao>
          <Cartao titulo={`Fontes citadas (${d.refs.length})`}>
            <ul className="flex flex-col gap-1 text-xs">
              {d.refs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-1">
                  <Link to="/fontes/$id" params={{ id: r.fonteId }} className="font-mono font-semibold text-primary">
                    {r.fonteId}
                  </Link>
                  {r.fatoId ? (
                    <Link to="/fatos/$id" params={{ id: r.fatoId }} className="font-mono text-muted hover:text-primary">
                      {r.fatoId}
                    </Link>
                  ) : null}
                  {r.pendente ? <Selo tom="alerta">pendente</Selo> : null}
                  <span className="w-full truncate font-mono text-muted">{r.caminho}</span>
                </li>
              ))}
            </ul>
          </Cartao>
          <Cartao titulo="Histórico">
            <ol className="flex flex-col gap-1.5 text-xs">
              {d.historico.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: histórico só de leitura
                <li key={i}>
                  <span className="font-semibold">{h.acao}</span> · {h.ator ?? "sistema"} ·{" "}
                  <span className="text-muted">{dataHora(h.em)}</span>
                </li>
              ))}
            </ol>
          </Cartao>
        </aside>
      </div>

      <Dialogo
        aberto={!!revisando}
        aoFechar={() => setRevisando(null)}
        titulo={revisando === "aprovar" ? "Aprovar ficha" : "Devolver ao editor"}
      >
        {revisando === "aprovar" ? (
          <p className="text-sm text-muted">
            A versão aprovada entra na próxima publicação. Você assina como CRMV-{eu?.uf} {eu?.crmv}.
          </p>
        ) : (
          <p className="text-sm text-muted">Explique o que precisa ser corrigido. A ficha volta para rascunho.</p>
        )}
        <Campo rotulo={revisando === "aprovar" ? "Comentário (opcional)" : "O que corrigir"}>
          <Area className="font-sans text-sm" rows={4} value={comentario} onChange={(e) => setComentario(e.target.value)} />
        </Campo>
        {revisar.error ? <Aviso>{msg(revisar.error)}</Aviso> : null}
        <div className="flex justify-end gap-2">
          <Botao onClick={() => setRevisando(null)}>Cancelar</Botao>
          <Botao
            variante={revisando === "aprovar" ? "primario" : "perigo"}
            disabled={revisar.isPending}
            onClick={() => revisando && revisar.mutate(revisando)}
          >
            {revisando === "aprovar" ? "Aprovar" : "Devolver"}
          </Botao>
        </div>
      </Dialogo>
    </>
  );
}
